import React, { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Lock, Eye, EyeOff, Loader, CheckCircle, AlertCircle } from 'lucide-react'
import { apiClient } from '@/services/api'
import styles from './AuthStandalone.module.css'

export function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.cardDanger}>
          <AlertCircle className={styles.dangerIcon} />
          <h1 className={styles.titleDanger}>INVALID LINK</h1>
          <p className={styles.dangerText}>
            This password reset link is invalid or has expired.
          </p>
          <Link to="/auth/forgot-password" className={styles.link}>
            REQUEST NEW LINK
          </Link>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== passwordConfirmation) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)
    try {
      await apiClient.confirmPasswordReset(token, password)
      setSuccess(true)
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.errors?.join(', ') ||
        'Failed to reset password. The link may have expired.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.emoji}>🥔⚓</div>
          <h1 className={styles.title}>
            {success ? 'PASSWORD RESET' : 'SET NEW PASSWORD'}
          </h1>
          <p className={styles.subtitle}>
            {success ? 'Your password has been updated.' : 'Choose a strong new password for your account.'}
          </p>
        </div>

        {error && (
          <div className={styles.errorAlert}>
            <AlertCircle className={styles.errorIcon} />
            <span className={styles.errorText}>{error}</span>
          </div>
        )}

        {success ? (
          <div className={styles.successCenter}>
            <CheckCircle className={styles.successIcon} />
            <p className={styles.successText}>
              You can now sign in with your new password.
            </p>
            <Link to="/auth" className={styles.link}>
              SIGN IN
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* New password */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>NEW PASSWORD</label>
              <div className={styles.inputContainer}>
                <Lock className={styles.inputIcon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className={styles.formInput}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.toggleBtn}
                >
                  {showPassword
                    ? <EyeOff className={styles.toggleIcon} />
                    : <Eye className={styles.toggleIcon} />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div className={styles.formGroupLast}>
              <label className={styles.formLabel}>CONFIRM PASSWORD</label>
              <div className={styles.inputContainer}>
                <Lock className={styles.inputIcon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  placeholder="Confirm new password"
                  className={styles.formInput}
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className={styles.submitBtn}
            >
              {loading ? (
                <>
                  <Loader className={styles.spinnerIcon} />
                  RESETTING...
                </>
              ) : (
                'RESET PASSWORD'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
